// Immodurchblick v2 — single Vite entry point.
//
// Stack:
//   - Alpine.js 3       : declarative interactivity (replaces jQuery, jQuery UI, AdminLTE JS)
//   - Chart.js 4        : charts (lazy-loaded per page that needs it)
//   - Day.js            : 2KB drop-in for moment
//
// Icons: not pulled in by the main bundle. When a page needs an icon, import
// the specific SVG inline (e.g. `import EyeIcon from '@tabler/icons/icons/eye.svg?raw'`)
// or use a small per-page entry. Keeps the auth pages tiny.
//
// CSS is imported once here so Vite extracts it into the manifest entry's
// "css" array — PHP uses {{ vite_css('resources/js/app.js') }} for the
// <link rel="stylesheet"> tag.

import '../css/app.css';
import 'nouislider/dist/nouislider.css';
import 'tom-select/dist/css/tom-select.css';

import Alpine from 'alpinejs';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import noUiSlider from 'nouislider';
import TomSelect from 'tom-select';

// Dashboard map data (Wien districts + Austria Bundeslaender). Exposed
// on `window.__maps` so the dashboard.twig inline scripts can pick the
// right region's geometry without an extra fetch.
import { wienDistricts, wienLabelOffsets, wienViewBox } from './maps/wien.js';
import { austriaBundeslaender, austriaCentroids, austriaLabelOffsets, austriaLabels, austriaViewBox } from './maps/austria.js';
import * as niederoesterreichMap from './maps/niederoesterreich.js';
import * as burgenlandMap from './maps/burgenland.js';
import * as oberoesterreichMap from './maps/oberoesterreich.js';
import * as steiermarkMap from './maps/steiermark.js';
import * as salzburgMap from './maps/salzburg.js';
import * as kaerntenMap from './maps/kaernten.js';
import * as tirolMap from './maps/tirol.js';
import * as vorarlbergMap from './maps/vorarlberg.js';

// Globalize a few things for templates that want to use them inline.
dayjs.locale('de');
window.dayjs = dayjs;

// Chart.js: register all controllers/scales/plugins once. The dashboard
// template references window.Chart in a small inline init script.
// datalabels is a global plugin (off by default) — pages opt in via
// `plugins: { datalabels: { display: true, ... } }` on individual charts.
Chart.register(...registerables);
Chart.register(ChartDataLabels);
Chart.defaults.plugins.datalabels = { display: false };
Chart.defaults.font.family = "'Source Sans 3', ui-sans-serif, system-ui, sans-serif";
Chart.defaults.color = '#4b5563';
window.Chart = Chart;

// Expose filter-UI libs globally for inline templates (Topobjekte filters).
window.noUiSlider = noUiSlider;
window.TomSelect = TomSelect;

// Dashboard map data — only the dashboard template touches this.
// Each entry exposes `paths` + `viewBox` at minimum. Optional `centroids` /
// `labels` / `labelOffsets` override the automatic bbox-centre placement.
// For die Bundesland-Sub-Maps doubles der Polygon-Key (Bezirksname) als
// Label; einzelne Bezirks-Labels können optional via `labelOffsets`-Export
// in der jeweiligen map-Datei nudge'd werden (z.B. resources/js/maps/
// kaernten.js für Villach-Land/Klagenfurt-Land).
const bundeslandMap = (m) => ({
    paths:        m.bezirke,
    viewBox:      m.viewBox,
    labels:       Object.fromEntries(Object.keys(m.bezirke).map(k => [k, k])),
    labelOffsets: m.labelOffsets || {},
});
window.__maps = {
    wien: {
        paths:        wienDistricts,
        viewBox:      wienViewBox,
        labelOffsets: wienLabelOffsets,
    },
    austria: {
        paths:        austriaBundeslaender,
        viewBox:      austriaViewBox,
        centroids:    austriaCentroids,
        labels:       austriaLabels,
        labelOffsets: austriaLabelOffsets,
    },
    niederoesterreich: bundeslandMap(niederoesterreichMap),
    burgenland:        bundeslandMap(burgenlandMap),
    oberoesterreich:   bundeslandMap(oberoesterreichMap),
    steiermark:        bundeslandMap(steiermarkMap),
    salzburg:          bundeslandMap(salzburgMap),
    kaernten:          bundeslandMap(kaerntenMap),
    tirol:             bundeslandMap(tirolMap),
    vorarlberg:        bundeslandMap(vorarlbergMap),
};

// Start Alpine. x-data + x-on/x-bind/x-show in Twig templates pick it up.
window.Alpine = Alpine;
Alpine.start();
